import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { Table, Spinner, Alert, Button, Badge, Modal, Form, FormControl, InputGroup, Dropdown } from "react-bootstrap";
import { FaEdit, FaTrash, FaUserPlus, FaUserMinus, FaShare, FaSearch, FaFilter } from "react-icons/fa";
import "../CSS/Client.css";

const EmployeeTable = () => {
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemoveClientModal, setShowRemoveClientModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", email: "" });
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [productFilter, setProductFilter] = useState("all");
  const [filteredEmployees, setFilteredEmployees] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
    fetchClients();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, employeeSearchTerm, clientSearchTerm, productFilter]);


  const fetchEmployees = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin-login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch employees");

      const data = await response.json();
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin-login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch clients");

      const data = await response.json();
      setClients(data);
    } catch (error) {
      setError(error.message);
    }
  };

  const filterEmployees = () => {
    let filtered = [...employees];

    // Filter by employee name
    if (employeeSearchTerm) {
      const term = employeeSearchTerm.toLowerCase();
      filtered = filtered.filter(employee =>
        employee.name.toLowerCase().includes(term)
      );
    }

    // Filter by client name
    if (clientSearchTerm) {
      const term = clientSearchTerm.toLowerCase();
      filtered = filtered.filter(employee =>
        employee.clients.some(client =>
          client.name.toLowerCase().includes(term)
        )
      );
    }

    // Filter by product
    if (productFilter !== "all") {
      filtered = filtered.filter(employee => {
        if (productFilter === "taxFiling") {
          return employee.clients.some(client => client.isTaxFilingEnabled);
        } else if (productFilter === "bookkeeping") {
          return employee.clients.some(client => client.isBookkeepingEnabled);
        }
        return true;
      });
    }

    setFilteredEmployees(filtered);
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setEditFormData({ name: employee.name, email: employee.email });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/${selectedEmployee._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) throw new Error("Failed to update employee");

      const updatedEmployee = await response.json();
      setEmployees(employees.map((emp) => (emp._id === updatedEmployee.employee._id ? updatedEmployee.employee : emp)));
      setShowEditModal(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDelete = async (employeeId) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      const token = localStorage.getItem("adminToken");

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/${employeeId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to delete employee");

        setEmployees(employees.filter((emp) => emp._id !== employeeId));
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleAssignClient = (employee) => {
    setSelectedEmployee(employee);
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async () => {
    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/assign-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ employeeId: selectedEmployee._id, clientId: selectedClient }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to assign client");
      }

      const data = await response.json();
      setEmployees(employees.map((emp) => (emp._id === data.employee._id ? data.employee : emp)));
      setShowAssignModal(false);
      setSelectedClient(null);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRemoveClient = (employee) => {
    setSelectedEmployee(employee);
    setShowRemoveClientModal(true);
  };

  const handleRemoveClientSubmit = async () => {
    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/remove-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ employeeId: selectedEmployee._id, clientId: selectedClient }),
      });

      if (!response.ok) throw new Error("Failed to remove client");

      const data = await response.json();
      setEmployees(employees.map((emp) => (emp._id === data.employee._id ? data.employee : emp)));
      setShowRemoveClientModal(false);
      setSelectedClient(null);
    } catch (error) {
      setError(error.message);
    }
  };

  // Function to copy the link to the clipboard
  const handleShare = () => {
    const link = "http://employee.bookscomply.com/";
    navigator.clipboard
      .writeText(link)
      .then(() => {
        alert("Link copied to clipboard: " + link);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
        alert("Failed to copy link.");
      });
  };

  return (
    <div className="admin-dashboard-container">
      <h2 className="dashboard-title mb-4">Employee Management</h2>

      {/* Share Button */}
      <div className="mb-4">
        <Button variant="success" onClick={handleShare}>
          <FaShare /> Add Employee
        </Button>
      </div>

      {/* Filter Controls */}
      <div className="mb-3 d-flex flex-wrap gap-3">
        {/* Employee Name Search */}
        <div style={{ width: "250px" }}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <FormControl
              placeholder="Search by employee name"
              value={employeeSearchTerm}
              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
            />
          </InputGroup>
        </div>

        {/* Client Name Search */}
        <div style={{ width: "250px" }}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <FormControl
              placeholder="Search by client name"
              value={clientSearchTerm}
              onChange={(e) => setClientSearchTerm(e.target.value)}
            />
          </InputGroup>
        </div>

        {/* Product Filter */}
        <Dropdown>
          <Dropdown.Toggle variant="secondary" id="dropdown-product-filter">
            <FaFilter /> Products: {productFilter === "all" ? "All" :
              productFilter === "taxFiling" ? "Tax Filing" : "Bookkeeping"}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setProductFilter("all")}>All Products</Dropdown.Item>
            <Dropdown.Item onClick={() => setProductFilter("taxFiling")}>Tax Filing</Dropdown.Item>
            <Dropdown.Item onClick={() => setProductFilter("bookkeeping")}>Bookkeeping</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading employees...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <div className="table-responsive excel-table">
            <Table striped bordered hover className="users-table">
              <thead className="table-header">
                <tr>
                  <th>Sr No.</th>
                  <th>Employee Name</th>
                  <th>Clients</th>
                  <th>Services</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee, index) => (
                  <tr key={employee._id}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="user-info">
                        <span className="full-name">
                          {employee.name}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="user-info">
                        {employee.clients.map((client) => (
                          <div key={client._id}>
                            <span>{client.name}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      {employee.clients.map((client) => (
                        <div key={client._id}>
                          {client.isTaxFilingEnabled && <Badge bg="success" className="badge-spacing">Tax Filing</Badge>}
                          {client.isBookkeepingEnabled && <Badge bg="info" className="badge-spacing">Bookkeeping</Badge>}
                        </div>
                      ))}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Button variant="info" size="sm" title="Edit" onClick={() => handleEdit(employee)}>
                          <FaEdit />
                        </Button>
                        <Button variant="danger" size="sm" title="Delete" onClick={() => handleDelete(employee._id)}>
                          <FaTrash />
                        </Button>
                        <Button variant="warning" size="sm" title="Remove Client" onClick={() => handleRemoveClient(employee)}>
                          <FaUserMinus />
                        </Button>
                        <Button variant="primary" size="sm" title="Assign Client" onClick={() => handleAssignClient(employee)}>
                          <FaUserPlus />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {filteredEmployees.length === 0 && (
              <div className="text-center p-4">
                <Alert variant="info">No employees match your filters</Alert>
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit Employee Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
              />
            </Form.Group>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Assign Client Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Client</Form.Label>
              <Form.Control
                as="select"
                value={selectedClient || ""}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <option value="">Select a client</option>
                {clients
                  .filter((client) => !employees.some((emp) => emp.clients.some((c) => c._id === client._id)))
                  .map((client) => (
                    <option key={client._id} value={client._id}>
                      {client.name}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleAssignSubmit}>
            Assign
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Remove Client Modal */}
      <Modal show={showRemoveClientModal} onHide={() => setShowRemoveClientModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Remove Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Client</Form.Label>
              <Form.Control
                as="select"
                value={selectedClient || ""}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <option value="">Select a client</option>
                {selectedEmployee?.clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveClientModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleRemoveClientSubmit}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default EmployeeTable;